import { Typography } from "@material-ui/core";
import _ from "lodash";
import React from "react";
import {
  BigQueryIcon,
  CallRailIcon,
  CustomerIOIcon,
  HubspotIcon,
  IntercomIcon,
  InvocaIcon,
  IterableIcon,
  JavascriptIcon,
  KustomerIcon,
  MailchimpIcon,
  PostgresqlIcon,
  ReactJsIcon,
  RedshiftIcon,
  SalesforceIcon,
  SendGridIcon,
  SendInBlueIcon,
  ServerSideIcon,
  ShopifyIcon,
  SnowflakeIcon,
  StripeIcon,
} from "../components/icons/themes/logos";
import { ConfigurableSource, Source } from "./types";

import { AndroidOsIcon, AppleLogoIcon } from "../components/icons/themes/color";
import { inDev } from "../config";

export class ConfigurableSourcePage {
  info: ConfigurableSource;

  constructor(info: ConfigurableSource) {
    this.info = info;
  }

  configPageUrl() {
    return `/sources/${this.info.slug}`;
  }

  name() {
    return this.info.name;
  }

  slug() {
    return this.info.slug;
  }

  logoSquare() {
    return this.info.logoSquare;
  }

  configBodyHelp() {
    return this.info.configBodyHelp;
  }
}

export var nonConfigurableSources: Source[] = [
  {
    key: "Web",
    name: "Web",
    logoSquare: JavascriptIcon,
    modal: "Web",
  },
  {
    key: "Server Side",
    name: "Server-side",
    logoSquare: ServerSideIcon,
    modal: "Server Side",
  },
  {
    key: "React Native",
    name: "React Native",
    logoSquare: ReactJsIcon,
    modal: "React Native",
  },
  {
    key: "Android",
    name: "Android",
    logoSquare: AndroidOsIcon,
    modal: "Android",
  },
  {
    key: "iOS",
    name: "iOS",
    logoSquare: AppleLogoIcon,
    modal: "iOS",
  },
  {
    key: "Sendinblue",
    name: "Sendinblue",
    logoSquare: SendInBlueIcon,
    modal: "Sendinblue",
  },
  {
    key: "Mailchimp",
    name: "Mailchimp",
    logoSquare: MailchimpIcon,
    modal: "Mailchimp",
  },
  {
    key: "Intercom",
    name: "Intercom",
    logoSquare: IntercomIcon,
    modal: "Intercom",
  },
  {
    key: "SendGrid",
    name: "SendGrid",
    logoSquare: SendGridIcon,
    modal: "SendGrid",
  },
  {
    key: "Customer.io",
    name: "Customer.io",
    logoSquare: CustomerIOIcon,
    modal: "Customer.io",
  },
  {
    key: "Kustomer",
    name: "Kustomer",
    logoSquare: KustomerIcon,
    modal: "Kustomer",
  },
  {
    key: "CallRail",
    name: "CallRail",
    logoSquare: CallRailIcon,
    modal: "CallRail",
  },
  {
    key: "Invoca",
    name: "Invoca",
    logoSquare: InvocaIcon,
    modal: "Invoca",
  },
];

export var configurableSources: ConfigurableSource[] = [
  {
    key: "BigQuery",
    name: "BigQuery",
    slug: "bigquery",
    logoSquare: BigQueryIcon,
    modal: "Contact Us",
    body: "Load data from BigQuery into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        BigQuery is a fully-managed, serverless data warehouse that enables
        scalable analysis over petabytes of data. Connect BigQuery to join,
        transform, and distribute your data with Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>Connect BigQuery to import your internal datasets into Freshpaint.</>
    ),
    configFields: [
      {
        name: "Project ID",
        key: "project_id",
        required: true,
        description: (
          <>
            Your BigQuery Project ID can be found{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://console.cloud.google.com/cloud-resource-manager"
            >
              here
            </a>
            .
          </>
        ),
      },
      {
        name: "JSON Credentials",
        key: "credentials_json",
        required: true,
        description: (
          <>
            To get JSON credentials for BigQuery, you first need to create a
            service account for Freshpaint{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://console.cloud.google.com/cloud-resource-manager"
            >
              here
            </a>{" "}
            and give the account the BigQuery User and BigQuery Data Editor
            Roles. Then add a JSON key to the service account.
          </>
        ),
      },
    ],
  },
  {
    key: "HubSpot",
    name: "HubSpot",
    slug: "hubspot",
    logoSquare: HubspotIcon,
    modal: "Contact Us",
    body: "Load data from HubSpot into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        HubSpot is a CRM - it lets you keep track of all your customer leads.
        With the Freshpaint-HubSpot integration, you can import contacts,
        accounts, and other information into Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>
        Obtain an access token by creating a private app under Settings {">"}{" "}
        Integrations {">"} Private Apps. See the{" "}
        <a
          className="text-brand-red underline"
          href="https://documentation.freshpaint.io/sources/hubspot"
          rel="noopener noreferrer"
          target="_blank"
        >
          documentation
        </a>{" "}
        to see which scopes to grant Freshpaint.
      </>
    ),
    configFields: [
      {
        name: "Access Token",
        key: "access_token",
        required: true,
        description: "",
      },
      {
        name: "Start Date",
        key: "start_date",
        required: true,
        description:
          "The date to start syncing data from. Should be in a format like 2022-01-30T00:00:00Z.",
      },
    ],
  },
  {
    key: "Iterable",
    name: "Iterable",
    slug: "iterable",
    logoSquare: IterableIcon,
    modal: "Contact Us",
    body: "Connect Iterable to import your customer data into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Iterable is a cross-channel marketing platform you can use to engage
        with your customers. With this source, you can import your customer data
        from Iterable to Freshpaint and send that data to any destinations.
      </Typography>
    ),
    configHelp: (
      <>
        To connect Iterable as a source to Freshpaint, you will need to{" "}
        <a
          className="text-brand-red underline"
          href="https://support.iterable.com/hc/en-us/articles/360043464871-API-Keys-"
          rel="noopener noreferrer"
          target="_blank"
        >
          create a new API key
        </a>{" "}
        or use an existing one &mdash; be sure to use a server-side key.
      </>
    ),
    configFields: [
      {
        name: "API Key",
        key: "api_key",
        sensitive: true,
        required: true,
      },
      {
        name: "Start Date",
        key: "start_date",
        required: true,
        description: (
          <>
            Enter a date in the form of{" "}
            <code className="text-xs">YYYY-MM-DDThh:mm:ssZ</code>, where{" "}
            <code className="text-xs">YYYY-MM-DD</code> is some date and{" "}
            <code className="text-xs">hh:mm:ss</code> is some time. Data on that
            date and time, and after, will be replicated. The time should be
            given in UTC.
          </>
        ),
      },
    ],
  },
  {
    key: "Postgres",
    name: "Postgres",
    slug: "postgres",
    logoSquare: PostgresqlIcon,
    modal: "Contact Us",
    body: "Load data from Postgres into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Postgres is a powerful open source relational database system. Connect
        Postgres to join, transform, and distribute your data with Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>Connect Postgres to import your internal datasets into Freshpaint.</>
    ),
    configFields: [
      {
        name: "Host",
        key: "host",
        required: true,
      },
      {
        name: "Port",
        key: "port",
        required: true,
        type: "number",
      },
      {
        name: "Database",
        key: "database",
        required: true,
      },
      {
        name: "Schemas",
        key: "schemas",
        required: true,
        description: "A comma separated list of schemas to pull tables from.",
        defaultValue: "public",
      },
      {
        name: "Username",
        key: "username",
        required: true,
      },
      {
        name: "Password",
        key: "password",
        required: true,
        sensitive: true,
      },
      {
        name: "Use SSL",
        key: "ssl",
        defaultValue: true,
        required: true,
        type: "boolean",
      },
    ],
  },
  {
    key: "Redshift",
    name: "Redshift",
    slug: "redshift",
    logoSquare: RedshiftIcon,
    modal: "Contact Us",
    body: "Load data from Redshift into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Amazon Redshift is a popular enterprise data warehouse. Connect Redshift
        to join, transform, and distribute your data with Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>Connect Redshift to import your internal datasets into Freshpaint.</>
    ),
    configFields: [
      {
        name: "Host",
        key: "host",
        required: true,
      },
      {
        name: "Port",
        key: "port",
        required: true,
        type: "number",
      },
      {
        name: "Database",
        key: "database",
        required: true,
      },
      {
        name: "Schemas",
        key: "schemas",
        required: true,
        description: "A comma separated list of schemas to pull tables from.",
        defaultValue: "public",
      },
      {
        name: "Username",
        key: "username",
        required: true,
      },
      {
        name: "Password",
        key: "password",
        required: true,
        sensitive: true,
      },
    ],
  },
  {
    key: "Salesforce",
    name: "Salesforce",
    slug: "salesforce",
    logoSquare: SalesforceIcon,
    modal: "Contact Us",
    body: "Connect Salesforce to import your CRM data into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Salesforce is a popular customer relationship management platform.
        Import your contacts, accounts, and any other user-centric data into
        Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>Connect Salesforce to import your CRM data into Freshpaint.</>
    ),
    configFields: [
      {
        type: "oauth",
        name: "Refresh Token",
        key: "refresh_token",
        integration_name: "source-salesforce",
        sensitive: true,
        required: true,
        oauthURL: (state: string, _params: Record<string, any>): string => {
          const clientId =
            "3MVG95jctIhbyCpoKLp7qUhuHESGwv0XXpbP4TSHM1G_rjaX9J8yV4x9Ct2LTwEtava7ftMENp3D2NVbz2AUF";
          const url = new URL(
            "https://login.salesforce.com/services/oauth2/authorize"
          );
          const searchParams = new URLSearchParams({
            response_type: "code",
            state: state,
            client_id: clientId,
            redirect_uri: inDev
              ? "http://localhost:3000/oauth"
              : "https://app.freshpaint.io/oauth",
          });
          url.search = searchParams.toString();
          return url.toString();
        },
      },
      {
        name: "Start Date",
        key: "start_date",
        required: false,
        description:
          "Enter a date in YYYY-MM-DD format. Data on that date and after will be replicated. By default all data is synced.",
      },
    ],
  },
  {
    key: "Shopify",
    name: "Shopify",
    slug: "shopify",
    logoSquare: ShopifyIcon,
    modal: "Contact Us",
    body: "Connect Shopify to import your eCommerce data into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Shopify is a popular platform for building ecommerce sites. Import your
        customers, orders, and any other user-centric data into Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>
        To connect Shopify to Freshpaint, you will need to create a Custom App.
        Check out our{" "}
        <a
          className="text-brand-red underline"
          href="https://documentation.freshpaint.io/sources/shopify"
          rel="noopener noreferrer"
          target="_blank"
        >
          Shopify documentation
        </a>{" "}
        to see how to do that.
      </>
    ),
    configFields: [
      {
        name: "Shopify Store Subdomain",
        key: "shop",
        required: true,
        description:
          "The subdomain of your Shopify Store. If the URL of your store is xyz.myshopify.com, the subdomain is xyz.",
      },
      {
        name: "Admin API Access Token",
        key: "api_password",
        sensitive: true,
        required: true,
      },
      {
        name: "Start Date",
        key: "start_date",
        required: true,
        description:
          "Enter a date in YYYY-MM-DD format. Data on that date and after will be replicated.",
      },
    ],
  },
  {
    key: "Snowflake",
    name: "Snowflake",
    slug: "snowflake",
    logoSquare: SnowflakeIcon,
    modal: "Contact Us",
    body: "Load data from Snowflake into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Snowflake is a popular cloud data warehouse. Connect Snowflake to join,
        transform, and distribute your data with Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>Connect Snowflake to import your internal datasets into Freshpaint.</>
    ),
    configFields: [
      {
        name: "Locator",
        key: "host",
        required: true,
        description: (
          <>
            The locator of your Snowflake instance. Can be found under Admin
            {" > "} Accounts. Some account names look like{" "}
            <code className="text-xs">abc12345.snowflakecomputing.com</code>;
            others might have a region, like{" "}
            <code className="text-xs">
              abc12345.us-east-1.snowflakecomputing.com
            </code>
            .
          </>
        ),
      },
      {
        name: "Username",
        key: "username",
        required: true,
      },
      {
        name: "Password",
        key: "password",
        required: true,
      },
      {
        name: "Role",
        key: "role",
        required: true,
      },
      {
        name: "Warehouse",
        key: "warehouse",
        required: true,
      },
      {
        name: "Database",
        key: "database",
        required: true,
        description:
          "Snowflake databases are case sensitive, and by default are uppercased when created. Unless you know for certain, you should enter your database in uppercase letters.",
      },
      {
        name: "Schema",
        key: "schema",
        required: true,
        description:
          "Snowflake schemas are case sensitive, and by default are uppercased when created. Unless you know for certain, you should enter your schema in uppercase letters.",
      },
    ],
  },
  {
    key: "Stripe",
    name: "Stripe",
    slug: "stripe",
    logoSquare: StripeIcon,
    modal: "Contact Us",
    body: "Load data from Stripe into Freshpaint",
    configBodyHelp: (
      <Typography variant="body2">
        Stripe is a popular credit card processor. Connect Stripe to pull your
        transaction and revenue data into Freshpaint.
      </Typography>
    ),
    configHelp: (
      <>
        Connect Stripe to import your transaction and revenue data into
        Freshpaint.
      </>
    ),
    configFields: [
      {
        name: "Account ID",
        key: "account_id",
        required: true,
        description: "Find it under Settings > Account details.",
      },
      {
        name: "Secret Key",
        key: "client_secret",
        required: true,
        description: "Find it under Developers > API keys.",
      },
      {
        name: "Start Date",
        key: "start_date",
        required: true,
        description:
          "The date to start syncing data from. Should be in a format like 2022-01-30T00:00:00Z.",
      },
    ],
  },
];
export var ConfigurableSourcePages: Record<string, ConfigurableSourcePage> = _(
  configurableSources
)
  .map((source) => new ConfigurableSourcePage(source))
  .keyBy((source) => source.slug())
  .value();

export var sources: Source[] = nonConfigurableSources.concat(
  configurableSources
);
